import { Native } from '@hypelab/sdk-react';
import { useRef, useState } from 'react';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import type { GetQuestOutput } from '@zealy/utils';
import { Button } from '@zealy/design-system';

import { RewardCard } from '#components/RewardCard';
import { useCommunityURL } from '#hooks/useCommunityURL';
import { useNextQuest } from '#hooks/useNextQuest';
import { flattenRewardMethod } from '#utils/flattenReward';
import { cn } from '#utils/utils';

import { ClaimReward } from './ClaimReward';
import { Base } from './StateBase';

type SuccessProps = {
  questId: string;
  moduleId: string;
  rewards: GetQuestOutput['rewards'];
  quest: GetQuestOutput;
};

export const Success = ({ rewards, moduleId, quest, questId }: SuccessProps) => {
  const t = useTranslations();
  const divRef = useRef<HTMLDivElement>(null);

  const [shouldShownClaimAnimation, setShouldShownClaimAnimation] = useState(false);
  const [isAdReady, setIsAdReady] = useState(false);
  const nextQuest = useNextQuest(moduleId, questId);
  const baseURL = useCommunityURL();
  const nextQuestURL = `${baseURL}/questboard/${nextQuest?.categoryId}/${nextQuest?.id}`;

  const handleAdReady = () => {
    setIsAdReady(true);
  };

  const handleAdError = () => {
    setIsAdReady(false);
  };

  const Confetti = dynamic(() => import('react-confetti'), {
    ssr: false,
  });

  const voteRewards = rewards.filter(reward => reward.method?.type === 'vote');
  const onlyVoteRewards = rewards.length === voteRewards.length;

  const isClaimed = shouldShownClaimAnimation;

  const onClaim = () => {
    setShouldShownClaimAnimation(true);
  };

  if (isClaimed) {
    return <ClaimReward questId={questId} moduleId={moduleId} rewards={rewards} quest={quest} />;
  }

  const getDescription = () => {
    if (voteRewards.length > 0 && onlyVoteRewards) {
      return 'vote-rewards';
    }
    if (voteRewards.length > 0 && !onlyVoteRewards) {
      return 'claim-vote-rewards';
    }
    return 'claim-rewards';
  };

  console.log('isAdReady', isAdReady);
  return (
    <Base
      title={t('communities.claim.completed.title')}
      description={`${t('communities.claim.completed.description.default')} ${t(
        `communities.claim.completed.description.${getDescription()}`,
        {
          count: rewards.length,
        },
      )}`}
      mascot="happy-jump"
      ref={divRef}
    >
      <div className="flex flex-col gap-200">
        <div className="flex flex-row gap-200 justify-center">
          {rewards.map((reward, index) => (
            <RewardCard
              size="sm"
              key={index.toString()}
              {...{ ...reward, ...flattenRewardMethod(reward.method) }}
              quest={quest}
            />
          ))}
        </div>

        {/* Native Ad */}
        <div
          className={cn(
            'flex flex-col gap-100 mt-300',
            !isAdReady && 'absolute opacity-0 pointer-events-none',
          )}
        >
          <p className="text-secondary body-paragraph text-center">
            Check out Zealy&apos;s partner
          </p>
          <Native placement={'b334893e45'} onReady={handleAdReady} onError={handleAdError}>
            <a
              data-ref="ctaLink"
              className="block w-full hover:opacity-90 transition-opacity cursor-pointer leading-none rounded-component-md overflow-hidden"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                data-ref="mediaContent"
                className="w-full aspect-video bg-component-tertiary block"
              />
            </a>
            {/* Hidden elements required by HypeLab SDK */}
            <div className="hidden">
              <img data-ref="icon" />
              <span data-ref="advertiser" />
              <p data-ref="headline" />
              <p data-ref="body" />
              <span data-ref="ctaText" />
            </div>
          </Native>
        </div>

        <div className="w-full flex justify-center">
          {rewards.length > 0 && !onlyVoteRewards && (
            <Button onClick={onClaim} color="cta" className="mt-300" size="lg">
              {t('common.claim-rewards', { count: rewards.length })}
            </Button>
          )}
          {voteRewards.length > 0 && (
            <Button
              color="cta"
              as={Link}
              href={`${baseURL}/questboard/${moduleId}/${questId}/submissions`}
            >
              {t('communities.claim.completed.vote-cta')}
            </Button>
          )}

          {rewards.length === 0 &&
            (nextQuest ? (
              <Button href={nextQuestURL} color="cta" as={Link} className="mt-300 w-min " size="lg">
                {t('common.next-quest')}
              </Button>
            ) : (
              <Button
                href={`${baseURL}/questboard`}
                color="cta"
                as={Link}
                className="mt-300 w-min"
                size="lg"
              >
                {t('quest.back')}
              </Button>
            ))}
        </div>

        <Confetti
          numberOfPieces={300}
          recycle={false}
          width={divRef.current?.offsetWidth}
          height={divRef.current?.offsetHeight}
        />
      </div>
    </Base>
  );
};
